/**
 * 📊 SPACE ANALYTICS - Module Sensors (Analytics)
 * Tableau de bord analytique style vaisseau spatial
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, TrendingUp, Users, ShoppingCart, Eye, Clock,
  Smartphone, Monitor, Tablet, Globe, BarChart3, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';

// Carte statistique
const SensorCard = ({ icon: Icon, title, value, change, color = 'cyan' }) => {
  const colorClasses = {
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
    green: 'border-green-500/30 bg-green-500/10 text-green-400',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
    orange: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
    pink: 'border-pink-500/30 bg-pink-500/10 text-pink-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl p-4 border ${colorClasses[color]}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {change && (
            <p className={`text-xs mt-1 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </motion.div>
  );
};

// Barre de progression
const ProgressBar = ({ label, value, max, color = 'cyan' }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  const colorClasses = {
    cyan: 'from-cyan-500 to-blue-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-yellow-500',
  };

  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full bg-gradient-to-r ${colorClasses[color]}`}
        />
      </div>
    </div>
  );
};

// Module Analytics principal
const SpaceAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [appInstalls, setAppInstalls] = useState({ total: 0, deviceStats: {}, browserStats: {}, dailyStats: {}, installs: [] });
  const [isLoading, setIsLoading] = useState(true);

  // Charger les données
  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const [statsData, installsData] = await Promise.all([
        api.getAdminStats?.() || {},
        api.getAppInstalls?.() || { total: 0, deviceStats: {}, browserStats: {}, installs: [] }
      ]);
      setStats(statsData);
      setAppInstalls(installsData);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const deviceIcons = {
    mobile: Smartphone,
    ios: Smartphone,
    desktop: Monitor,
    tablet: Tablet,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-500">
            📊 SENSORS
          </h1>
          <p className="text-gray-500 text-sm">Analyse des données en temps réel</p>
        </div>
        <Button
          onClick={loadAnalytics}
          variant="outline"
          className="border-green-500/30 text-green-400"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Scanner
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Scan en cours...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SensorCard icon={Users} title="Clients totaux" value={stats?.totalCustomers || 0} color="cyan" />
            <SensorCard icon={ShoppingCart} title="Cartes actives" value={stats?.activeCards || 0} color="green" />
            <SensorCard icon={Activity} title="Activité 24h" value={stats?.last24hActivity || 0} color="purple" />
            <SensorCard icon={TrendingUp} title="Croissance" value={`${stats?.growthRate || 0}%`} color="orange" />
          </div>

          {/* Installations PWA */}
          <div className="backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/30 bg-[#12121a]/60">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-cyan-400" />
              📲 Installations PWA
            </h3>

            {/* Compteur principal */}
            <div className="flex justify-center mb-6">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center px-8 py-6 rounded-2xl bg-cyan-500/10 border-2 border-cyan-500/50"
              >
                <Smartphone className="w-12 h-12 mx-auto mb-2 text-cyan-400" />
                <div className="text-5xl font-black text-white">{appInstalls.total}</div>
                <div className="text-cyan-400 text-sm">installations totales</div>
              </motion.div>
            </div>

            {/* Stats par appareil */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {Object.entries(appInstalls.deviceStats || {}).map(([device, count]) => {
                const Icon = deviceIcons[device] || Globe;
                return (
                  <div key={device} className="bg-[#1a1a25] rounded-xl p-3 text-center border border-gray-700">
                    <Icon className="w-6 h-6 mx-auto mb-1 text-cyan-400" />
                    <div className="text-xl font-bold text-white">{count}</div>
                    <div className="text-xs text-gray-500 capitalize">{device}</div>
                  </div>
                );
              })}
            </div>

            {/* Stats par navigateur */}
            {Object.keys(appInstalls.browserStats || {}).length > 0 && (
              <div className="bg-[#1a1a25] rounded-xl p-4 mb-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  Par navigateur
                </h4>
                {Object.entries(appInstalls.browserStats || {}).map(([browser, count]) => (
                  <ProgressBar 
                    key={browser} 
                    label={browser} 
                    value={count} 
                    max={appInstalls.total || 1}
                    color="cyan"
                  />
                ))}
              </div>
            )}

            {/* 7 derniers jours */}
            {Object.keys(appInstalls.dailyStats || {}).length > 0 && (
              <div className="bg-[#1a1a25] rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-400" />
                  7 derniers jours
                </h4>
                <div className="flex items-end gap-2 h-24">
                  {Object.entries(appInstalls.dailyStats || {}).map(([date, count]) => {
                    const maxCount = Math.max(...Object.values(appInstalls.dailyStats));
                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div key={date} className="flex-1 flex flex-col items-center">
                        <motion.div 
                          initial={{ height: 0 }}
                          animate={{ height: `${Math.max(height, 5)}%` }}
                          className="w-full bg-gradient-to-t from-cyan-500 to-blue-500 rounded-t"
                        />
                        <span className="text-[10px] text-gray-500 mt-1">
                          {new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Dernières installations */}
          {(appInstalls.installs || []).length > 0 && (
            <div className="backdrop-blur-xl rounded-2xl p-5 border border-purple-500/30 bg-[#12121a]/60">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-400" />
                📋 Dernières installations
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {appInstalls.installs.slice(0, 10).map((install) => {
                  const Icon = deviceIcons[install.deviceType] || Globe;
                  return (
                    <div key={install.id} className="flex items-center justify-between p-3 rounded-xl bg-[#1a1a25] border border-gray-700">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-white text-sm">{install.browser} • {install.platform || install.deviceType}</p>
                          <p className="text-xs text-gray-500">
                            {install.screenWidth}x{install.screenHeight} • {install.language}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(install.createdAt).toLocaleDateString('fr-FR', { 
                          day: '2-digit', 
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpaceAnalytics;
